import { ChevronDownIcon } from "lucide-react";
import { useMemo, useState } from "react";
import {
  composeRenderProps,
  Menu,
  MenuItem,
  MenuItemProps,
  MenuSection,
  MenuTrigger,
} from "react-aria-components";
import { Key, Selection, useMultipleSelectionState } from "react-stately";
import { twMerge } from "tailwind-merge";
import { Button } from "./Button";
import { Checkbox } from "./Checkbox";
import { Popover } from "./Popover";
import { Separator } from "./Separator";
import { focusRing } from "./utils";

type MultipleSelectionStateProps = Parameters<
  typeof useMultipleSelectionState
>[0];

export type SelectFilterStateItem<T> = {
  key: Key;
  label?: string;
} & (unknown extends T ? { value?: T } : { value: T });

export interface SelectFilterStateProps<T>
  extends Omit<
    MultipleSelectionStateProps,
    "allowDuplicateSelectionEvents" | "disabledBehavior" | "selectionBehavior"
  > {
  items?: Iterable<SelectFilterStateItem<T>>;
}

export function useSelectFilterState<T>({
  items: itemsIterable,
  ...props
}: SelectFilterStateProps<T>) {
  const multipleSelectionState = useMultipleSelectionState({
    defaultSelectedKeys: props.defaultSelectedKeys,
    disabledKeys: props.disabledKeys,
    disallowEmptySelection: props.disallowEmptySelection,
    selectedKeys: props.selectedKeys,
    selectionMode: props.selectionMode,
    onSelectionChange: props.onSelectionChange,
    allowDuplicateSelectionEvents: false,
    disabledBehavior: "all",
  });

  const { selectedKeys, setSelectedKeys, selectionMode } =
    multipleSelectionState;

  const { items, itemsArray, itemsByKey } = useMemo(() => {
    const itemsByKey = new Map<Key, SelectFilterStateItem<T>>();
    if (itemsIterable) {
      for (const item of itemsIterable) {
        itemsByKey.set(item.key, item);
      }
    }
    const items = new Set(itemsByKey.values());
    const itemsArray = [...itemsByKey.values()];
    return {
      items,
      itemsArray,
      itemsByKey,
    };
  }, [itemsIterable]);

  const { selectedItems, selectedItemsArray } = useMemo(() => {
    const selectedItemsArray =
      selectedKeys === "all"
        ? itemsArray
        : itemsArray.filter((item) => selectedKeys.has(item.key));
    const selectedItems = new Set(selectedItemsArray);
    return {
      selectedItems,
      selectedItemsArray,
    };
  }, [itemsArray, selectedKeys]);

  const selectionLabel = useMemo(() => {
    if (selectedItemsArray.length === itemsByKey.size) return "Alle";
    if (selectedItemsArray.length === 0) return "Alle";
    if (selectedItemsArray.length === 1)
      return selectedItemsArray[0].label ?? selectedItemsArray[0].key;
    return `${selectedItemsArray.length} valgt`;
  }, [selectedItemsArray, itemsByKey]);

  return {
    multipleSelectionState,
    selectedKeys,
    setSelectedKeys,
    selectionMode,
    items,
    itemsArray,
    itemsByKey,
    selectedItems,
    selectedItemsArray,
    selectionLabel,
  };
}

export function useSelectFilter<T>(props: {
  items: SelectFilterStateItem<T>[];
  defaultSelectedKeys?: Selection;
}) {
  const itemsByKey = useMemo(
    () => new Map(props.items.map((item) => [item.key, item])),
    [props.items],
  );

  const [selectedKeys, setSelectedKeys] = useState<Selection>(
    props.defaultSelectedKeys ?? new Set(),
  );

  const { selectedItems, selectedValues } = useMemo(() => {
    const selectedItems =
      selectedKeys === "all"
        ? props.items
        : props.items.filter((item) => selectedKeys.has(item.key));

    const selectedValues = selectedItems.map<SelectFilterStateItem<T>["value"]>(
      (item) => item.value,
    );

    return { selectedItems, selectedValues };
  }, [props.items, selectedKeys]);

  return {
    items: props.items,
    itemsByKey,
    selectedKeys,
    setSelectedKeys,
    selectedItems,
    selectedValues,
  };
}

export interface SelectFilterProps<T> extends SelectFilterStateProps<T> {
  buttonLabel?: string;
  "aria-label"?: string;
}

export function SelectFilter<T>({
  buttonLabel,
  ...props
}: SelectFilterProps<T>) {
  const selectFilterState = useSelectFilterState(props);

  const {
    itemsArray,
    itemsByKey,
    selectedItems,
    setSelectedKeys,
    selectionMode,
    selectionLabel,
  } = selectFilterState;

  return (
    <MenuTrigger trigger="press">
      <Button
        className="-mr-2 -ml-3 h-8 min-w-0 pr-2 pl-3 text-[11px]/[16px] font-medium"
        intent="tertiary"
        size="sm"
        aria-label={props["aria-label"] ?? buttonLabel}
      >
        <div className="flex items-center gap-x-1">
          {buttonLabel ? (
            <span className="text-theme-on-background-variant mr-1">
              {buttonLabel}
            </span>
          ) : null}
          <span>{selectionLabel}</span>
          <ChevronDownIcon size={16} />
        </div>
      </Button>

      <Popover
        placement="bottom right"
        className="bg-theme-glass backdrop-blur-glass text-theme-on-background w-[--trigger-width] overflow-auto rounded-md p-3 text-[11px]/[16px]"
        trigger="MenuTrigger"
        offset={4}
      >
        <Menu selectionMode={selectionMode}>
          {selectionMode === "multiple" && (
            <>
              <MenuSection
                id="selectAllItems"
                selectionMode="multiple"
                selectedKeys={[]}
                onSelectionChange={() => {
                  setSelectedKeys(
                    selectedItems.size === itemsByKey.size
                      ? new Set()
                      : new Set(itemsByKey.keys()),
                  );
                }}
              >
                <SelectFilterItem
                  id="neverDo"
                  showCheckbox={true}
                  checkboxIsIndeterminate={
                    selectedItems.size > 0 &&
                    selectedItems.size < itemsByKey.size
                  }
                  checkboxIsSelected={selectedItems.size === itemsByKey.size}
                >
                  Vælg alle
                </SelectFilterItem>
              </MenuSection>

              <Separator className="my-3" />
            </>
          )}

          <MenuSection
            id="items"
            {...selectFilterState}
            items={itemsArray}
            onSelectionChange={(keys) => {
              if (
                keys !== "all" &&
                !keys.size &&
                selectFilterState.multipleSelectionState.disallowEmptySelection
              ) {
                return;
              }
              setSelectedKeys(keys);
            }}
          >
            {({ key, label }) => (
              <SelectFilterItem
                id={key}
                showCheckbox={selectionMode === "multiple"}
              >
                {label ?? key}
              </SelectFilterItem>
            )}
          </MenuSection>
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}

export function SelectFilterItem<T>({
  className,
  showCheckbox,
  checkboxIsIndeterminate,
  checkboxIsSelected,
  ...props
}: MenuItemProps<SelectFilterStateItem<T>> & {
  showCheckbox?: boolean;
  checkboxIsIndeterminate?: boolean;
  checkboxIsSelected?: boolean;
}) {
  return (
    <MenuItem
      {...props}
      className={(renderProps) =>
        twMerge(
          focusRing(renderProps),
          "hover:bg-theme-on-background-hover flex h-11 cursor-pointer items-center gap-x-3 rounded-md px-3 font-medium text-nowrap transition-[background-color] duration-200",
          renderProps.selectionMode === "single" &&
            renderProps.isSelected &&
            "text-theme-active",
          composeRenderProps(className, (className) => className)(renderProps),
        )
      }
    >
      {(renderProps) => (
        <>
          {showCheckbox && (
            <>
              <Checkbox
                size="sm"
                className="pointer-events-none -m-1 size-7 justify-center"
                isSelected={checkboxIsSelected ?? renderProps.isSelected}
                isIndeterminate={checkboxIsIndeterminate}
              />
            </>
          )}

          <div className="truncate">
            {composeRenderProps(
              props.children,
              (children) => children,
            )(renderProps)}
          </div>
        </>
      )}
    </MenuItem>
  );
}
